import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { JitsiCallProvider, useJitsiCall } from './contexts/JitsiCallContext';
import KSLoading from './components/common/KSLoading';
import { SocialChallengeProvider } from './contexts/SocialChallengeContext';
import { StreakProvider } from './contexts/StreakContext';
import { NotificationProvider } from './contexts/NotificationContext';
import IncomingChallengeModal from './components/challenge/IncomingChallengeModal';
import StreakNotificationModal from './components/streak/StreakNotificationModal';
import NotificationSoftPromptModal from './components/notification/NotificationSoftPromptModal';
import ConnectionStatusBanner from './components/ConnectionStatusBanner';
import DataConsentModal from './components/consent/DataConsentModal';
import './App.css';
import VerticalNav from './components/header/VerticalNav';

// Lazy-loaded routes and navigation components for optimal bundle splitting
const AdminNav = lazy(() => import('./components/header/AdminNav'));
const CoachNav = lazy(() => import('./components/header/CoachNav'));
const SuperCoachNav = lazy(() => import('./components/header/SuperCoachNav'));
const Home = lazy(() => import('./pages/home/Home'));
const LoginPage = lazy(() => import('./pages/login/LoginPage'));
const JitsiMeetPanel = lazy(() => import('./features/classroom/JitsiMeetPanel'));
const ChessBoard = lazy(() => import('./components/chessboard/ChessBoard'));
const BoardEditor = lazy(() => import('./components/board-editor/BoardEditor'));
const Connect = lazy(() => import('./pages/connect'));
const Learn = lazy(() => import('./pages/learn'));
const FindCoaches = lazy(() => import('./pages/find-coaches'));
const Puzzles = lazy(() => import('./pages/puzzles'));
const Openings = lazy(() => import('./pages/openings'));
const PlayOnline = lazy(() => import('./pages/play-online'));
const GameViewer = lazy(() => import('./components/game-viewer/GameViewer'));
const Pricing = lazy(() => import('./pages/pricing'));
const AiPlanPage = lazy(() => import('./pages/pricing/AiPlanPage'));
const ChessBotTest = lazy(() => import('./pages/computer'));
const Endgame = lazy(() => import('./pages/endgame'));
const AdminDashboard = lazy(() => import('./features/admin/AdminDashboard'));
const AdminLesson = lazy(() => import('./features/admin/AdminEndgameLesson'));
const AdminProtectedRoute = lazy(() => import('./features/admin/AdminProtectedRoute'));
const AdminLogin = lazy(() => import('./features/admin/AdminLogin'));
const AdminUsers = lazy(() => import('./features/admin/AdminUsers'));
const AdminPuzzles = lazy(() => import('./features/admin/AdminTopicPuzzles'));
const AdminTopics = lazy(() => import('./features/admin/AdminTopics'));
const AdminEndgameTopics = lazy(() => import('./features/admin/AdminEndgameTopics'));
const AdminMessages = lazy(() => import('./features/admin/AdminMessages'));
const AdminOpenings = lazy(() => import('./features/admin/AdminOpenings'));
const AdminDailyPuzzles = lazy(() => import('./features/admin/AdminDailyPuzzles'));
const AdminSettings = lazy(() => import('./features/admin/AdminSettings'));
const CoachLogin = lazy(() => import('./features/coach/CoachLogin'));
const CoachProtectedRoute = lazy(() => import('./features/coach/CoachProtectedRoute'));
const CoachDashboard = lazy(() => import('./features/coach/CoachDashboard'));
const SuperCoachProtectedRoute = lazy(() => import('./features/super-coach/SuperCoachProtectedRoute'));
const SuperCoachDashboard = lazy(() => import('./features/super-coach/SuperCoachDashboard'));
const AcademyLoginPage = lazy(() => import('./features/super-coach/AcademyLoginPage'));
const CoachManagement = lazy(() => import('./features/admin/CoachManagement'));
const AcademyManagement = lazy(() => import('./features/admin/AcademyManagement'));
const AdminRecycleBin = lazy(() => import('./features/admin/AdminRecycleBin'));
const AdminBookings = lazy(() => import('./features/admin/AdminBookings'));
const AdminVoices = lazy(() => import('./features/admin/AdminVoices'));
const AdminPayments = lazy(() => import('./features/admin/AdminPayments'));
const AdminCoachPayouts = lazy(() => import('./features/admin/AdminCoachPayouts'));
const AdminBots = lazy(() => import('./features/admin/AdminBots'));
const Profile = lazy(() => import('./pages/profile/profile-page'));
const CoachStudentDashboard = lazy(() => import('./features/coach/CoachStudentDashboard'));
const Classroom = lazy(() => import('./pages/classroom'));
const Vision = lazy(() => import('./pages/vision'));
const ProfileEdit = lazy(() => import('./pages/edit-profile/edit-profile'));
const LessonTest = lazy(() => import('./features/lessonMap/lesson'));
const AdminRoadmapManager = lazy(() => import('./features/admin/AdminRoadmapManager'));
const AdminTierZeroManager = lazy(() => import('./features/admin/AdminTierZeroManager'));
const About = lazy(() => import('./pages/about'));
const Privacy = lazy(() => import('./pages/privacy'));
const DeleteAccount = lazy(() => import('./pages/delete-account'));
const ArenaTournamentPage = lazy(() => import('./pages/arena'));
const VerifyEmailPage = lazy(() => import('./pages/verify-email/VerifyEmailPage'));
const FriendsPage = lazy(() => import('./pages/friends'));
const BlogsPage = lazy(() => import('./pages/blogs/BlogsPage'));
const BlogDetailPage = lazy(() => import('./pages/blogs/BlogDetailPage'));
const AdminBlogs = lazy(() => import('./features/admin/AdminBlogs'));
const NotificationsPage = lazy(() => import('./pages/notifications'));
const AdminNotifications = lazy(() => import('./features/admin/AdminNotifications'));

const PageFallback = () => <KSLoading />;

/** Dismisses the initial HTML splash screen once the active route finishes mounting */
function SplashDismiss() {
  React.useEffect(() => {
    const splash = document.getElementById('ks-app-splash');
    if (splash) {
      splash.classList.add('fade-out');
      const timer = setTimeout(() => {
        if (splash && splash.parentNode) {
          splash.parentNode.removeChild(splash);
        }
      }, 550);
      return () => clearTimeout(timer);
    }
  }, []);
  return null;
}

function PrivateRoute({ element }) {
  const { currentUser, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!currentUser) return <Navigate to="/home" state={{ from: location }} replace />;
  return element;
}

/** Renders the floating Jitsi panel globally — persists across all route changes */
function GlobalJitsiPanel() {
  const { callConfig, endCall } = useJitsiCall();
  if (!callConfig) return null;
  return (
    <JitsiMeetPanel
      classroomId={callConfig.classroomId}
      displayName={callConfig.displayName}
      isCoach={callConfig.isCoach}
      onClose={endCall}
    />
  );
}

function App() {
  const { currentUser, loading } = useAuth();
  const location = useLocation();
  const [isNavCollapsed, setIsNavCollapsed] = React.useState(false);

  const isAcademyRoute = location.pathname.startsWith('/academy') || location.pathname.startsWith('/portal');
  const hideNavbarRoutes = ['/login', '/admin-login', '/coach-login', '/academy-login', '/admin/login', '/coach/login', '/academy/login'];
  const shouldHideNavbar = hideNavbarRoutes.includes(location.pathname) || isAcademyRoute;
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isCoachRoute = location.pathname.startsWith('/coach') || location.pathname.startsWith('/coach-dashboard');
  const isSuperCoachRoute = location.pathname.startsWith('/super-coach');

  // Ensure body classes from login/auth pages are cleared whenever navigating to regular pages
  React.useEffect(() => {
    if (!shouldHideNavbar) {
      document.body.classList.remove('login-page', 'no-navbar');
    }
  }, [location.pathname, shouldHideNavbar]);

  return (
    <JitsiCallProvider>
      <SocialChallengeProvider>
        <StreakProvider>
          <NotificationProvider>
            <div className="app">
              <IncomingChallengeModal />
              <StreakNotificationModal />
              <NotificationSoftPromptModal />
              <DataConsentModal />
          {!shouldHideNavbar && (
            isAdminRoute ? (
              <Suspense fallback={null}>
                {currentUser?.is_admin ? (
                  <AdminNav onCollapseChange={setIsNavCollapsed} />
                ) : null}
              </Suspense>
            ) : isSuperCoachRoute ? (
              <Suspense fallback={null}>
                {(currentUser?.is_super_coach || currentUser?.is_admin) ? (
                  <SuperCoachNav onCollapseChange={setIsNavCollapsed} />
                ) : null}
              </Suspense>
            ) : isCoachRoute ? (
              <Suspense fallback={null}>
                {(currentUser?.is_coach || currentUser?.is_super_coach || currentUser?.is_admin) ? (
                  <CoachNav onCollapseChange={setIsNavCollapsed} />
                ) : null}
              </Suspense>
            ) : (
              <VerticalNav onCollapseChange={setIsNavCollapsed} />
            )
          )}
          <GlobalJitsiPanel />
          <main className={shouldHideNavbar ? "main-content-full" : `main-content ${isNavCollapsed ? 'collapsed' : ''}`}>
          <Suspense fallback={<PageFallback />}>
          <SplashDismiss />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/home" element={<Home />} />
            <Route path="/analysis" element={<ChessBoard />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/board-editor" element={<BoardEditor />} />
            <Route path="/connect" element={<Connect />} />
            <Route path="/learn" element={<Learn />} />
            <Route path="/learn/:topicId" element={<Learn />} />
            <Route path="/learn/:topicId/:puzzleId" element={<Learn />} />
            <Route path="/find-coaches" element={<FindCoaches />} />
            <Route path="/find-coaches/:coachId" element={<FindCoaches />} />
            <Route path="/coaches/:coachId" element={<FindCoaches />} />
            <Route path="/coach-profile/:coachId" element={<FindCoaches />} />
            <Route path="/puzzles" element={<Puzzles />} />
            <Route path="/openings" element={<Openings />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/plan" element={<AiPlanPage />} />
            <Route path="/ai-plan" element={<AiPlanPage />} />
            <Route path="/computer" element={<ChessBotTest />} />
            <Route path="/computer/:gameId" element={<ChessBotTest />} />
            <Route path="/game-viewer/:gameId" element={<PrivateRoute element={<GameViewer />} />} />
            <Route path="/tactics" element={<Learn />} />
            <Route path="/tactics/:topicId" element={<Learn />} />
            <Route path="/tactics/:topicId/:puzzleId" element={<Learn />} />
            <Route path="/endgame" element={<Endgame />} />
            <Route path="/endgame/:topicId" element={<Endgame />} />
            <Route path="/endgame/:topicId/:lessonId" element={<Endgame />} />
            <Route path="/endgame/lesson/:lessonId" element={<Endgame />} />
            <Route path="/friends" element={<FriendsPage />} />
            <Route path="/play-online-friends" element={<PlayOnline />} />
            <Route path="/play-with-friend" element={<PlayOnline />} />
            <Route path="/play-with-friends" element={<PlayOnline />} />
          <Route path="/tournament" element={<ArenaTournamentPage />} />
          <Route path="/tournament/index.html" element={<Navigate to="/tournament" replace />} />
          <Route path="/tournament/:id" element={<ArenaTournamentPage />} />
          <Route path="/arena" element={<ArenaTournamentPage />} />
          <Route path="/arena/index.html" element={<Navigate to="/arena" replace />} />
          <Route path="/arena/:id" element={<ArenaTournamentPage />} />
          <Route path="/classroom" element={<Classroom />} />
          <Route path="/vision" element={<Vision />}></Route>
          <Route path="/edit-profile" element={<PrivateRoute element={<ProfileEdit />} />} />
          <Route path="/profile/edit" element={<PrivateRoute element={<ProfileEdit />} />} />
          <Route path="/lesson-test" element={<LessonTest />} />
          <Route path="/lessons" element={<LessonTest />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/privacy/chess-clock" element={<Privacy defaultApp="chess-clock" />} />
          <Route path="/privacy/chess-clock.html" element={<Privacy defaultApp="chess-clock" />} />
          <Route path="/privacy/chess-clock/index.html" element={<Privacy defaultApp="chess-clock" />} />
          <Route path="/privacy/clock" element={<Privacy defaultApp="chess-clock" />} />
          <Route path="/chess-clock-privacy" element={<Privacy defaultApp="chess-clock" />} />
          <Route path="/chess-clock-privacy.html" element={<Privacy defaultApp="chess-clock" />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/delete-account/index.html" element={<DeleteAccount />} />
          <Route path="/delete-account.html" element={<DeleteAccount />} />
          <Route path="/account-deletion" element={<DeleteAccount />} />
          <Route path="/account-deletion/index.html" element={<DeleteAccount />} />
          <Route path="/account-deletion.html" element={<DeleteAccount />} />
          <Route path="/blogs" element={<BlogsPage />} />
          <Route path="/blogs/index.html" element={<Navigate to="/blogs" replace />} />
          <Route path="/blogs.html" element={<Navigate to="/blogs" replace />} />
          <Route path="/blogs/index" element={<Navigate to="/blogs" replace />} />
          <Route path="/blog/index.html" element={<Navigate to="/blogs" replace />} />
          <Route path="/blog/:slug" element={<BlogDetailPage />} />
          <Route path="/blogs/:slug" element={<BlogDetailPage />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/profile/:username" element={<Profile />} />
          <Route path="/notifications" element={<PrivateRoute element={<NotificationsPage />} />} />
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminProtectedRoute />}>
            <Route index element={<AdminDashboard />} />
            <Route path="notifications" element={<AdminNotifications />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="roadmap" element={<AdminRoadmapManager />} />
            <Route path="tier-0" element={<AdminTierZeroManager />} />
            <Route path="blogs" element={<AdminBlogs />} />
            <Route path="bots" element={<AdminBots />} />
            <Route path="recycle-bin" element={<AdminRecycleBin />} />
            <Route path="endgame-lesson" element={<AdminLesson />} />
            <Route path="combat-lesson" element={<Navigate to="/admin/endgame-lesson" replace />} />
            <Route path="coaches" element={<CoachManagement />} />
            <Route path="academies" element={<AcademyManagement />} />
            <Route path="messages" element={<AdminMessages />} />
            <Route path="openings" element={<AdminOpenings />} />
            <Route path="puzzles" element={<AdminPuzzles />} />
            <Route path="daily-puzzles" element={<AdminDailyPuzzles />} />
            <Route path="topics" element={<AdminTopics />} />
            <Route path="endgame-topics" element={<AdminEndgameTopics />} />
            <Route path="combat-topics" element={<Navigate to="/admin/endgame-topics" replace />} />
            <Route path="bookings" element={<AdminBookings />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="coach-payouts" element={<AdminCoachPayouts />} />
            <Route path="ai-voices" element={<AdminVoices />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>
          <Route path="/coach-login" element={<CoachLogin />} />
          <Route path="/coach/login" element={<CoachLogin />} />
          <Route path="/coach" element={<CoachProtectedRoute />}>
            <Route index element={<CoachDashboard />} />
            <Route path="students" element={<CoachDashboard defaultTab="students" />} />
            <Route path="batches" element={<CoachDashboard defaultTab="batches" />} />
            <Route path="homework" element={<CoachDashboard defaultTab="homework" />} />
            <Route path="content" element={<CoachDashboard defaultTab="content" />} />
            <Route path="blogs" element={<CoachDashboard defaultTab="blogs" />} />
            <Route path="schedule" element={<CoachDashboard defaultTab="calendar" />} />
            <Route path="attendance" element={<CoachDashboard defaultTab="attendance" />} />
            <Route path="referrals" element={<CoachDashboard defaultTab="referrals" />} />
            <Route path="bookings" element={<CoachDashboard defaultTab="bookings" />} />
            <Route path="pricing" element={<CoachDashboard defaultTab="finances" />} />
            <Route path="finances" element={<CoachDashboard defaultTab="finances" />} />
            <Route path="offerings" element={<CoachDashboard defaultTab="finances" />} />
            <Route path="earnings" element={<CoachDashboard defaultTab="finances" />} />
            <Route path="student-dashboard/:studentId" element={<CoachStudentDashboard />} />
          </Route>
          <Route path="/coach-dashboard" element={<CoachProtectedRoute />}>
            <Route index element={<CoachDashboard />} />
            <Route path="students" element={<CoachDashboard defaultTab="students" />} />
            <Route path="batches" element={<CoachDashboard defaultTab="batches" />} />
            <Route path="homework" element={<CoachDashboard defaultTab="homework" />} />
            <Route path="content" element={<CoachDashboard defaultTab="content" />} />
            <Route path="blogs" element={<CoachDashboard defaultTab="blogs" />} />
            <Route path="schedule" element={<CoachDashboard defaultTab="calendar" />} />
            <Route path="attendance" element={<CoachDashboard defaultTab="attendance" />} />
            <Route path="referrals" element={<CoachDashboard defaultTab="referrals" />} />
            <Route path="bookings" element={<CoachDashboard defaultTab="bookings" />} />
            <Route path="pricing" element={<CoachDashboard defaultTab="finances" />} />
            <Route path="finances" element={<CoachDashboard defaultTab="finances" />} />
            <Route path="offerings" element={<CoachDashboard defaultTab="finances" />} />
            <Route path="earnings" element={<CoachDashboard defaultTab="finances" />} />
            <Route path="student-dashboard/:studentId" element={<CoachStudentDashboard />} />
          </Route>
          <Route path="/super-coach" element={<SuperCoachProtectedRoute />}>
            <Route index element={<SuperCoachDashboard />} />
            <Route path="dashboard" element={<SuperCoachDashboard />} />
            <Route path="student-dashboard/:studentId" element={<CoachStudentDashboard />} />
          </Route>

          {/* Dedicated White-Label Academy Student & Coach Portals */}
          <Route path="/academy/:subdomain/login" element={<AcademyLoginPage />} />
          <Route path="/academy/:subdomain" element={<AcademyLoginPage />} />
          <Route path="/academy-login/:subdomain" element={<AcademyLoginPage />} />
          <Route path="/portal/:subdomain" element={<AcademyLoginPage />} />
          <Route path="/portal/:subdomain/login" element={<AcademyLoginPage />} />
          <Route path="/academy-login" element={<AcademyLoginPage />} />
          <Route path="/academy/login" element={<AcademyLoginPage />} />
        </Routes>
        </Suspense>
        </main>
        <ConnectionStatusBanner />
      </div>
          </NotificationProvider>
        </StreakProvider>
    </SocialChallengeProvider>
  </JitsiCallProvider>
  );
}

export default App;
